import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { axiosInstance } from '@/utils/axios';

import { mapDepartmentsFromDTO } from '../mappers/department.mapper';
import { Department } from '../types/department';
import {
  DeleteDepartmentRequest,
  UpdateDepartmentRequestItem,
  UpdateOrganizationDepartmentsRequest,
} from '../types/request/department-request';
import { DepartmentWithMemberCountResponse } from '../types/response/department-with-member-count-response';

/**
 * Fetches all departments for the organization
 * Returns complete list in single request (no pagination)
 */
export const getDepartments = async (): Promise<Department[]> => {
  const endpoint = '/api/departments';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: DepartmentWithMemberCountResponse[] }>(endpoint)
  );

  return mapDepartmentsFromDTO(response);
};

/**
 * Updates departments in bulk (create new, update existing)
 * Sends only departments with valid names
 */
export const updateDepartments = async (
  departments: UpdateDepartmentRequestItem[]
): Promise<Department[]> => {
  const endpoint = '/api/departments';

  // Filter out new departments (negative IDs) and include only those with names
  const departmentsToUpdate = departments
    .filter(dept => dept.id && dept.id > 0)
    .map(dept => ({
      id: dept.id,
      name: dept.name,
    }));

  // Include new departments (without ID)
  const newDepartments = departments
    .filter(dept => !dept.id || dept.id < 0)
    .map(dept => ({
      name: dept.name,
    }));

  const requestBody: UpdateOrganizationDepartmentsRequest = {
    departments: [...departmentsToUpdate, ...newDepartments],
  };

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.patch<{ data: DepartmentWithMemberCountResponse[] }>(endpoint, requestBody)
  );

  return mapDepartmentsFromDTO(response);
};

/**
 * Deletes a department and optionally transfers members to another department
 */
export const deleteDepartment = async ({
  departmentId,
  replacementDepartmentId,
}: {
  departmentId: number;
  replacementDepartmentId?: number;
}): Promise<void> => {
  const endpoint = '/api/departments';

  const requestBody: DeleteDepartmentRequest = {
    departmentId,
    ...(replacementDepartmentId !== undefined && { replacementDepartmentId }),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.delete(endpoint, { data: requestBody })
  );
};
